import { Pipe, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { PipeTransform } from '@angular/core';

export interface InterpolateParams {
  [param: string]: unknown;
  defaultValue?: string;
}

/**
 * This pipe can be used when translation is not available for some of the keys.
 * When translation is not found, the value provided in defaultValue is returned. If defaultValue
 * is not defined, then the last element from the Key after '.' is returned back, see examples.
 *
 * @param {string} translationId The Key from translation file
 * @param {object=} params  Filter parameters
 *
 * @returns {string} translated value
 *
 * @example
 *
 * Example #1: Assume translation file has this content for 'fr-CA' locale:
 * action: {
 *            'BUY': 'ACHAT',
 *            'SELL': 'VENTE'
 *        }
 *
 * {{'action.' + row.action | tdWbTranslate : { defaultValue: row.action} }}
 *          - when row.action is 'BUY' output is 'ACHAT';
 *          - when row.action is 'DIV' output is 'DIV';
 *
 * Example #2: Without defining defaultValue in filter parameter, the Key is parsed and
 * the value after last '.' is returned:
 * {{'action.' + row.action | tdWbTranslate }}
 *          - when row.action is 'BUY' output is 'ACHAT';
 *          - when row.action is 'DIV' output is 'DIV';
 */
@Pipe({
  name: 'tdWbTranslate'
})
export class TranslatePipe implements PipeTransform {
  private readonly translateService = inject<TranslateService>(TranslateService);

  transform(key: string | undefined | null, params?: InterpolateParams | Array<InterpolateParams>): string {
    const flattenParams: InterpolateParams = this.flattenErrorParams(params);

    let translatedValue = this.translateService.instant(key, flattenParams);
    if (translatedValue === key) { // not defined in resource file
      translatedValue =  flattenParams?.defaultValue ?? key.split('.').pop();
    }
    return translatedValue;
  }

  private flattenErrorParams(params?: InterpolateParams | Array<InterpolateParams>): InterpolateParams {
    if ((Array.isArray(params) && params.length > 0)) {
      const initialValue: InterpolateParams = {};
      return params.reduce((obj: InterpolateParams, item: InterpolateParams): InterpolateParams => ({ ...obj, ...item }), initialValue);
    }
    return params as InterpolateParams;
  }

}
